import { useState, useEffect, useCallback } from "react";
import { Download, RefreshCw, FileText, CheckCircle, AlertTriangle, Building2, Calendar, ShieldCheck, ArrowUpRight, ArrowDownLeft, Layers, Percent, DollarSign, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import GSTNSetupDialog from "@/components/GSTNSetupDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (amount) => {
  if (amount === undefined || amount === null) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatLakhs = (amount) => {
  if (!amount) return "₹0 L";
  const lakhs = amount / 100000;
  return `₹${lakhs.toFixed(2)} L`;
};

const GSTReport = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [companyCode, setCompanyCode] = useState("1000");
  const [period, setPeriod] = useState("09/2026");
  const [activeTab, setActiveTab] = useState("overview");

  const [summary, setSummary] = useState(null);
  const [gstr1Data, setGstr1Data] = useState([]);
  const [gstr2bData, setGstr2bData] = useState([]);
  const [gstWarnings, setGstWarnings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, gstr1Res, gstr2bRes] = await Promise.all([
        axios.get(`${API}/gst/summary`, { params: { company_code: companyCode, period } }),
        axios.get(`${API}/gst/gstr1`, { params: { company_code: companyCode, period } }),
        axios.get(`${API}/gst/gstr2b`, { params: { company_code: companyCode, period } }),
      ]);
      setSummary(sumRes.data.summary);
      setGstr1Data(gstr1Res.data.items || []);
      setGstr2bData(gstr2bRes.data.items || []);
      setGstWarnings(sumRes.data.warnings || []);
      setLastRefreshedAt(new Date());
    } catch (error) {
      console.error("Failed to fetch GST data:", error);
      toast.error("Failed to load GST Report data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyCode, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/gst/export`, {
        params: { company_code: companyCode, period },
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `GST_Report_${companyCode}_${period.replace("/", "-")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("SAP GST draft exported to Excel successfully!");
    } catch (error) {
      console.error("Failed to export Excel:", error);
      toast.error("Failed to export GST Excel report");
    } finally {
      setExporting(false);
    }
  };

  const filteredGstr1 = gstr1Data.filter(
    (item) =>
      item.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.invoice_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.customer_gstin?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.hsn_sac?.includes(searchQuery)
  );

  const filteredGstr2b = gstr2bData.filter(
    (item) =>
      item.supplier_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.invoice_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.supplier_gstin?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.hsn_sac?.includes(searchQuery)
  );

  return (
    <div className="space-y-6" data-testid="gst-report-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="heading-display text-3xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
            <FileText className="w-7 h-7 text-primary" />
            GST Report & Compliance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            SAP GST draft register for the selected company and return period
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">SAP Draft Data</Badge>
            <span className="text-xs text-muted-foreground">GSTR-2B GSTN reconciliation is not yet synced.</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={companyCode} onValueChange={setCompanyCode}>
            <SelectTrigger className="w-[160px] h-9" data-testid="select-company-code">
              <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Company Code" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1000">1000 - Maharashtra</SelectItem>
              <SelectItem value="2000">2000 - Telangana</SelectItem>
              <SelectItem value="3000">3000 - Karnataka</SelectItem>
            </SelectContent>
          </Select>

          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px] h-9" data-testid="select-period">
              <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="09/2026">Sept 2026</SelectItem>
              <SelectItem value="08/2026">Aug 2026</SelectItem>
              <SelectItem value="07/2026">Jul 2026</SelectItem>
            </SelectContent>
          </Select>

          {isAdmin && <GSTNSetupDialog companyCode={companyCode} onSaved={fetchData} />}

          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="h-9" data-testid="btn-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>


          <Button size="sm" onClick={handleExportExcel} disabled={exporting} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-export-excel">
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export SAP Draft"}
          </Button>
        </div>
      </div>

      {/* Executive Stat Cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-md" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gst-stat-cards">
          {/* Card 1: Outward Sales Taxable */}
          <Card className="card-industrial border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase">Outward Sales Taxable</span>
                <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold mt-2 text-foreground">{formatLakhs(summary?.outward_taxable_value)}</p>
              <p className="text-xs text-muted-foreground mt-1">{formatINR(summary?.outward_taxable_value)}</p>
            </CardContent>
          </Card>

          {/* Card 2: Output GST Liability */}
          <Card className="card-industrial border-l-4 border-l-indigo-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase">Total Output GST</span>
                <div className="w-8 h-8 rounded-md bg-indigo-50 flex items-center justify-center">
                  <Percent className="w-4 h-4 text-indigo-600" />
                </div>
              </div>
              <p className="text-2xl font-bold mt-2 text-indigo-700">{formatLakhs(summary?.total_output_gst)}</p>
              <p className="text-xs text-muted-foreground mt-1">CGST + SGST + IGST Output</p>
            </CardContent>
          </Card>

          {/* Card 3: SAP input tax draft */}
          <Card className="card-industrial border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase">SAP Input Tax Draft</span>
                <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center">
                  <ArrowDownLeft className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-bold mt-2 text-emerald-700">{formatLakhs(summary?.total_input_itc)}</p>
              <p className="text-xs text-muted-foreground mt-1">Requires GSTR-2B reconciliation</p>
            </CardContent>
          </Card>

          {/* Card 4: Draft tax difference */}
          <Card className="card-industrial border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase">Draft Tax Difference</span>
                <div className="w-8 h-8 rounded-md bg-amber-50 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                </div>
              </div>
              <p className="text-2xl font-bold mt-2 text-amber-700">{formatLakhs(summary?.net_total_payable)}</p>
              <p className="text-xs text-muted-foreground mt-1">Not a GSTR-3B filing amount</p>
            </CardContent>
          </Card>
        </div>
      )}

      {!loading && gstWarnings.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="gst-draft-warning">
          <span className="font-semibold">GST draft notice: </span>{gstWarnings[0]}
        </div>
      )}

      {!loading && lastRefreshedAt && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground" data-testid="gst-data-source">
          <span className="font-medium text-foreground">Data source: SAP draft</span>
          <span aria-hidden="true">•</span>
          <span>Loaded {lastRefreshedAt.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
        </div>
      )}


      {/* Main Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-2">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="overview" data-testid="tab-overview">Executive Overview</TabsTrigger>
            <TabsTrigger value="gstr1" data-testid="tab-gstr1">GSTR-1 Outward Sales</TabsTrigger>
            <TabsTrigger value="gstr2b" data-testid="tab-gstr2b">SAP Inward Register</TabsTrigger>
            <TabsTrigger value="gstr3b" data-testid="tab-gstr3b">GSTR-3B Draft</TabsTrigger>
          </TabsList>

          {(activeTab === "gstr1" || activeTab === "gstr2b") && (
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search party, GSTIN, HSN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-xs"
                data-testid="input-gst-search"
              />
            </div>
          )}
        </div>

        {/* TAB 1: Executive Overview */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="card-industrial">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600" />
                  Output GST Tax Component Breakdown
                </CardTitle>
                <CardDescription>Intrastate vs Interstate sales tax split</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Central GST (CGST Output)</span>
                  <span className="font-semibold text-foreground">{formatINR(summary?.cgst_output)}</span>
                </div>
                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                  <div className="bg-blue-600 h-full rounded-full" style={{ width: summary?.total_output_gst ? `${Math.min(100, Math.round(((summary?.cgst_output || 0) / summary.total_output_gst) * 100))}%` : "0%" }} />
                </div>

                <div className="flex items-center justify-between text-sm pt-2">
                  <span className="text-muted-foreground">State GST (SGST Output)</span>
                  <span className="font-semibold text-foreground">{formatINR(summary?.sgst_output)}</span>
                </div>
                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                  <div className="bg-indigo-600 h-full rounded-full" style={{ width: summary?.total_output_gst ? `${Math.min(100, Math.round(((summary?.sgst_output || 0) / summary.total_output_gst) * 100))}%` : "0%" }} />
                </div>

                <div className="flex items-center justify-between text-sm pt-2">
                  <span className="text-muted-foreground">Integrated GST (IGST Output)</span>
                  <span className="font-semibold text-foreground">{formatINR(summary?.igst_output)}</span>
                </div>
                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                  <div className="bg-amber-600 h-full rounded-full" style={{ width: summary?.total_output_gst ? `${Math.min(100, Math.round(((summary?.igst_output || 0) / summary.total_output_gst) * 100))}%` : "0%" }} />
                </div>
              </CardContent>
            </Card>

            <Card className="card-industrial">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  Compliance & Filing Status
                </CardTitle>
                <CardDescription>GST Return Readiness Status for {period}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3 p-3 rounded-md bg-emerald-50 border border-emerald-200">
                  <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">GSTR-1 Outward Sales Status</p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      {gstr1Data.length > 0
                        ? `All ${gstr1Data.length} SAP billing documents verified with valid HSN codes & GSTINs.`
                        : `No SAP outward billing documents returned for Company ${companyCode} in ${period}.`}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-md bg-blue-50 border border-blue-200">
                  <CheckCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">SAP Inward Tax Status</p>
                    <p className="text-xs text-blue-700 mt-0.5">
                      {gstr2bData.length > 0
                        ? `${gstr2bData.length} SAP supplier invoices are ready for GSTR-2B reconciliation.`
                        : `No SAP supplier purchase invoices returned for Company ${companyCode} in ${period}.`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 2: GSTR-1 Outward Sales */}
        <TabsContent value="gstr1">
          <Card className="card-industrial">
            <CardHeader className="px-4 py-3 border-b border-border flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">GSTR-1 Outward Sales & Tax Invoices</CardTitle>
                <CardDescription>Sales invoices, Debit/Credit Notes & Customer GSTIN details</CardDescription>
              </div>
              <Badge variant="outline" className="text-xs font-semibold">{filteredGstr1.length} Invoices</Badge>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/40">
                    <TableHead className="w-[100px]">Invoice No</TableHead>
                    <TableHead className="w-[100px]">Date</TableHead>
                    <TableHead>Customer Name</TableHead>
                    <TableHead>Customer GSTIN</TableHead>
                    <TableHead>POS State</TableHead>
                    <TableHead>HSN/SAC</TableHead>
                    <TableHead className="text-right">Taxable Base</TableHead>
                    <TableHead className="text-right">CGST</TableHead>
                    <TableHead className="text-right">SGST</TableHead>
                    <TableHead className="text-right">IGST</TableHead>
                    <TableHead className="text-right">Total Amount</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGstr1.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                        No sales invoices found for the selected filter.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGstr1.map((item) => (
                      <TableRow key={item.invoice_no} className="hover:bg-muted/30">
                        <TableCell className="font-mono text-xs font-semibold text-primary">{item.invoice_no}</TableCell>
                        <TableCell className="text-xs">{item.posting_date}</TableCell>
                        <TableCell className="text-xs font-medium">{item.customer_name}</TableCell>
                        <TableCell className="font-mono text-xs"><Badge variant="outline" className="text-[11px] font-mono">{item.customer_gstin}</Badge></TableCell>
                        <TableCell className="text-xs">{item.place_of_supply}</TableCell>
                        <TableCell className="font-mono text-xs">{item.hsn_sac}</TableCell>
                        <TableCell className="text-right font-medium text-xs">{formatINR(item.taxable_value)}</TableCell>
                        <TableCell className="text-right text-xs">{formatINR(item.cgst_amount)}</TableCell>
                        <TableCell className="text-right text-xs">{formatINR(item.sgst_amount)}</TableCell>
                        <TableCell className="text-right text-xs">{formatINR(item.igst_amount)}</TableCell>
                        <TableCell className="text-right font-bold text-xs">{formatINR(item.total_amount)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">
                            {item.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: SAP Inward Purchase Register */}
        <TabsContent value="gstr2b">
          <Card className="card-industrial">
            <CardHeader className="px-4 py-3 border-b border-border flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">SAP Inward Purchases Register</CardTitle>
                <CardDescription>Supplier invoices awaiting official GSTR-2B reconciliation</CardDescription>
              </div>
              <Badge variant="outline" className="text-xs font-semibold">{filteredGstr2b.length} Invoices</Badge>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/40">
                    <TableHead className="w-[120px]">Vendor Invoice</TableHead>
                    <TableHead className="w-[100px]">Date</TableHead>
                    <TableHead>Supplier Name</TableHead>
                    <TableHead>Supplier GSTIN</TableHead>
                    <TableHead>POS State</TableHead>
                    <TableHead>HSN/SAC</TableHead>
                    <TableHead className="text-right">Taxable Base</TableHead>
                    <TableHead className="text-right">CGST</TableHead>
                    <TableHead className="text-right">SGST</TableHead>
                    <TableHead className="text-right">IGST</TableHead>
                    <TableHead className="text-right">Total Amount</TableHead>
                    <TableHead className="text-center">ITC Eligibility</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGstr2b.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                        No supplier invoices found for the selected filter.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGstr2b.map((item) => (
                      <TableRow key={item.invoice_no} className="hover:bg-muted/30">
                        <TableCell className="font-mono text-xs font-semibold text-blue-600">{item.invoice_no}</TableCell>
                        <TableCell className="text-xs">{item.posting_date}</TableCell>
                        <TableCell className="text-xs font-medium">{item.supplier_name}</TableCell>
                        <TableCell className="font-mono text-xs"><Badge variant="outline" className="text-[11px] font-mono">{item.supplier_gstin}</Badge></TableCell>
                        <TableCell className="text-xs">{item.place_of_supply}</TableCell>
                        <TableCell className="font-mono text-xs">{item.hsn_sac}</TableCell>
                        <TableCell className="text-right font-medium text-xs">{formatINR(item.taxable_value)}</TableCell>
                        <TableCell className="text-right text-xs">{formatINR(item.cgst_amount)}</TableCell>
                        <TableCell className="text-right text-xs">{formatINR(item.sgst_amount)}</TableCell>
                        <TableCell className="text-right text-xs">{formatINR(item.igst_amount)}</TableCell>
                        <TableCell className="text-right font-bold text-xs">{formatINR(item.total_amount)}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]">
                            {item.itc_eligibility}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: GSTR-3B Draft */}
        <TabsContent value="gstr3b">
          <Card className="card-industrial">
            <CardHeader className="px-4 py-3 border-b border-border">
              <CardTitle className="text-base">GSTR-3B Draft Computation Grid</CardTitle>
                  <CardDescription>Not filing-ready. GSTN reconciliation, ITC utilisation, reversals, and filing status are not included.</CardDescription>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/40">
                    <TableHead>Tax Details & Description</TableHead>
                    <TableHead className="text-right">Outward Taxable Base</TableHead>
                    <TableHead className="text-right">Output GST Liability</TableHead>
                    <TableHead className="text-right">SAP Input Tax Draft</TableHead>
                    <TableHead className="text-right">SAP Draft Difference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium text-xs">1. Central GST (CGST)</TableCell>
                    <TableCell className="text-right text-xs">{formatINR(summary?.outward_taxable_value)}</TableCell>
                    <TableCell className="text-right text-xs font-semibold text-blue-700">{formatINR(summary?.cgst_output)}</TableCell>
                    <TableCell className="text-right text-xs font-semibold text-emerald-700">{formatINR(summary?.cgst_input)}</TableCell>
                    <TableCell className="text-right text-xs font-bold text-amber-700">{formatINR(summary?.net_cgst_payable)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium text-xs">2. State / UT GST (SGST)</TableCell>
                    <TableCell className="text-right text-xs">{formatINR(summary?.outward_taxable_value)}</TableCell>
                    <TableCell className="text-right text-xs font-semibold text-blue-700">{formatINR(summary?.sgst_output)}</TableCell>
                    <TableCell className="text-right text-xs font-semibold text-emerald-700">{formatINR(summary?.sgst_input)}</TableCell>
                    <TableCell className="text-right text-xs font-bold text-amber-700">{formatINR(summary?.net_sgst_payable)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium text-xs">3. Integrated GST (IGST)</TableCell>
                    <TableCell className="text-right text-xs">{formatINR(summary?.outward_taxable_value)}</TableCell>
                    <TableCell className="text-right text-xs font-semibold text-blue-700">{formatINR(summary?.igst_output)}</TableCell>
                    <TableCell className="text-right text-xs font-semibold text-emerald-700">{formatINR(summary?.igst_input)}</TableCell>
                    <TableCell className="text-right text-xs font-bold text-amber-700">{formatINR(summary?.net_igst_payable)}</TableCell>
                  </TableRow>
                  <TableRow className="bg-secondary/30 font-bold">
                    <TableCell className="text-xs uppercase">Total Return Liability</TableCell>
                    <TableCell className="text-right text-xs">{formatINR(summary?.outward_taxable_value)}</TableCell>
                    <TableCell className="text-right text-xs text-blue-800">{formatINR(summary?.total_output_gst)}</TableCell>
                    <TableCell className="text-right text-xs text-emerald-800">{formatINR(summary?.total_input_itc)}</TableCell>
                    <TableCell className="text-right text-xs text-amber-800">{formatINR(summary?.net_total_payable)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GSTReport;
